import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const TYPE_LABELS = { initiative: "Iniciativa", pilot: "Piloto" };

export function PersonAssignmentsDialog({ open, onOpenChange, person }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!person) return;
    setLoading(true);
    try {
      setItems(await api.listPersonalAssignments(token, { persona_id: person.id }));
    } catch (e) {
      toast.error("Error al cargar asignaciones");
    } finally {
      setLoading(false);
    }
  }, [person, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleDelete = async (id) => {
    try {
      await api.deletePersonalAssignment(token, id);
      toast.success("Asignación eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  if (!person) return null;
  const totalHoras = items.reduce((s, a) => s + (a.horas || 0), 0);
  const totalCoste = items.reduce((s, a) => s + (a.coste_total || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="person-assignments-dialog">
        <DialogHeader>
          <DialogTitle>Asignaciones de {person.nombre} {person.apellidos}</DialogTitle>
          <DialogDescription>Total: {totalHoras}h · {totalCoste.toFixed(2)} € (planificación, no retroactivo)</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="person-assignments-empty">Sin asignaciones todavía.</p>
        ) : (
          <div className="space-y-2">
            {items.map((a) => (
              <div key={a.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`assignment-row-${a.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">{TYPE_LABELS[a.entity_type]}</Badge>
                    <span className="truncate">{a.entity_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{a.horas}h × {a.coste_hora_aplicado}€/h = {a.coste_total}€ · {a.created_by_name}</div>
                  {a.notas && <div className="text-xs text-muted-foreground italic mt-0.5">{a.notas}</div>}
                </div>
                <Button size="icon" variant="ghost" className="text-red-600 shrink-0" onClick={() => handleDelete(a.id)} data-testid={`assignment-delete-btn-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
